import { useIsVisible } from '@folklore/hooks';
import classNames from 'classnames';
import queryString from 'query-string';
import { ForwardedRef, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';

import { AuthResponse, useAuthProviderLogin } from '../../hooks/useAuth';
import useCurrentUrl from '../../hooks/useCurrentUrl';
import { mergeRefs } from '../../lib/utils';

import { useApi } from '../../contexts/ApiContext';
import { useIsCheckingAuth } from '../../contexts/AuthContext';
import { useFacebookSdk, useFacebookToken } from '../../contexts/FacebookContext';
import { useGoogleSdk, useGoogleToken } from '../../contexts/GoogleContext';
import { useRequestBase } from '../../contexts/RequestContext';
import LoadingIcon from '../icons/LoadingIcon';
import FacebookIcon from '../icons/socials/FacebookIcon';
import GoogleIcon from '../icons/socials/GoogleIcon';
import { BasicButtonElement } from './BasicButton';
import RoundedButton, { RoundedButtonProps } from './RoundedButton';

import styles from '../../styles/buttons/sso-button.module.css';

const providers = [
    {
        id: 'facebook',
        label: 'Facebook',
        icon: FacebookIcon,
    },
    {
        id: 'google',
        label: 'Google',
        icon: GoogleIcon,
    },
] as const;

export interface SsoButtonProps extends Omit<RoundedButtonProps, 'onClick'> {
    provider: 'facebook' | 'google';
    nextUrl?: string | null;
    subscription?: string | string[] | null;
    source?: string | null;
    size?: 'small' | 'medium' | 'large';
    theme?: 'dark' | 'light';
    loading?: 'lazy' | null;
    iconOnly?: boolean;
    onComplete?: ((user: AuthResponse) => void) | null;
    onClick?: ((e: MouseEvent<BasicButtonElement>) => void) | null;
    children?: ReactNode;
}

function setForwardedRef(
    ref: ForwardedRef<BasicButtonElement> | null,
    value: BasicButtonElement | null,
) {
    if (typeof ref === 'function') {
        ref(value);
        return;
    }

    if (ref !== null) {
        ref.current = value;
    }
}

function SsoButton({
    provider,
    nextUrl = null,
    source = null,
    subscription = null,
    size = 'medium',
    theme = 'dark',
    loading = 'lazy',
    iconOnly = false,
    className = null,
    onComplete = null,
    onClick = null,
    ref: buttonRef = null,
    ...props
}: SsoButtonProps & { ref?: ForwardedRef<BasicButtonElement> | null }) {
    const api = useApi();

    const isLazyLoading = loading === 'lazy';
    const { ref: visibleRef, visible: isVisible } = useIsVisible({
        rootMargin: '200px',
        persist: true,
        disabled: !isLazyLoading,
    });

    const shouldLoad = !isLazyLoading || isVisible;

    const { loginAsync, isPending: isLoading = false } = useAuthProviderLogin(provider);
    const isCheckingAuth = useIsCheckingAuth();
    const absoluteNextUrl = useCurrentUrl({ withSearch: true });
    const { baseUrl: authBaseUrl = null } = api.auth;
    const { label, icon: Icon } = providers.find(({ id }) => id === provider) || {};
    const finalNextUrl = nextUrl || absoluteNextUrl;
    const query = queryString.stringify({
        next: finalNextUrl,
        source,
        subscription,
    });

    const facebookSdk = useFacebookSdk();
    const facebookToken = useFacebookToken();
    const googleSdk = useGoogleSdk();
    const googleToken = useGoogleToken();
    const sdkButtonRef = useRef<HTMLDivElement | null>(null);
    const [clicked, setClicked] = useState(false);

    useEffect(() => {
        if (!shouldLoad) {
            return;
        }

        if (provider === 'facebook' && facebookSdk !== null) {
            facebookSdk.XFBML.parse(sdkButtonRef.current);
        } else if (provider === 'google' && googleSdk !== null) {
            googleSdk.id.renderButton(sdkButtonRef.current, {
                theme: theme === 'dark' ? 'filled_black' : 'outline',
                size,
                type: iconOnly ? 'icon' : 'standard',
                text: 'signin',
                click_listener: () => {
                    setClicked(true);
                },
            });
        }
    }, [provider, facebookSdk, googleSdk, size, iconOnly, theme, shouldLoad]);

    const loadingRef = useRef(false);
    useEffect(() => {
        let token = null;
        if (provider === 'facebook') {
            token = facebookToken;
        } else if (provider === 'google') {
            token = googleToken;
        }

        if (isCheckingAuth || token === null || !clicked || loadingRef.current) {
            return () => {};
        }

        loadingRef.current = true;
        let canceled = false;

        loginAsync({
            token,
            source,
            subscription,
        }).then((newUser) => {
            loadingRef.current = false;
            setClicked(false);
            if (onComplete !== null && !canceled) {
                onComplete(newUser);
            }
        });

        return () => {
            canceled = true;
        };
    }, [
        provider,
        facebookToken,
        googleToken,
        source,
        subscription,
        isCheckingAuth,
        onComplete,
        clicked,
        loginAsync,
    ]);

    const onClickFacebook = (e: MouseEvent<BasicButtonElement>) => {
        setClicked(true);
        facebookSdk.login(() => {}, { scope: 'public_profile,email' });
        onClick?.(e);
    };

    if (provider === 'google') {
        return (
            <div
                className={classNames([
                    styles.external,
                    styles[provider],
                    styles[size],
                    {
                        [styles.iconOnly]: iconOnly,
                    },
                    className,
                ])}
                ref={visibleRef}
            >
                <div ref={sdkButtonRef} />
                {isLoading ? (
                    <div className={styles.loading}>
                        <LoadingIcon className={styles.icon} />
                    </div>
                ) : null}
            </div>
        );
    }

    if (provider === 'facebook') {
        return (
            <RoundedButton
                ref={mergeRefs(buttonRef, visibleRef)}
                className={classNames([
                    styles.container,
                    styles[size],
                    styles[theme],
                    {
                        [styles.iconOnly]: iconOnly,
                    },
                    className,
                ])}
                onClick={onClickFacebook}
                {...props}
            >
                <span className={styles.iconContainer}>
                    {isLoading ? (
                        <LoadingIcon className={classNames([styles.icon, styles.loading])} />
                    ) : (
                        Icon && <Icon className={styles.icon} />
                    )}
                </span>
                <span className={styles.label}>Se connecter</span>
            </RoundedButton>
        );
    }

    return (
        <RoundedButton
            href={`${authBaseUrl}/${provider}?${query}`}
            external
            target="_self"
            ref={mergeRefs(buttonRef, visibleRef)}
            className={classNames([styles.container, className])}
            onClick={onClick}
            {...props}
        >
            <span className={styles.iconContainer}>{Icon && <Icon className={styles.icon} />}</span>
            <span className={styles.label}>{label}</span>
        </RoundedButton>
    );
}

export default SsoButton;
